import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosConfig";

const actionStyles = {
  CREATE: "bg-green-100 text-green-800",
  UPDATE: "bg-blue-100 text-blue-800",
  POST: "bg-purple-100 text-purple-800",
  VOID: "bg-red-100 text-red-800"
};

const formatDate = (date) =>
  new Date(date).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric"
  });

const formatDateTime = (date) =>
  new Date(date).toLocaleString();

const groupByDate = (logs) => {
  return logs.reduce((acc, log) => {
    const dateKey = formatDate(log.performedAt);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(log);
    return acc;
  }, {});
};

const AuditLogTab = ({ entityType, entityId, apiEndpoint }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!entityId) return;

    const fetchAudit = async () => {
      try {
        setLoading(true);
        
        // Use custom endpoint if provided, otherwise use generic audit trail
        const endpoint = apiEndpoint || `/audit-trail?entityType=${entityType}&entityId=${entityId}`;
        const res = await axiosInstance.get(endpoint);
        
        setLogs(res?.data?.data || res?.data || []);
      } catch (err) {
        console.error("Failed to load audit trail:", err);
        setError("Failed to load audit trail");
      } finally {
        setLoading(false);
      }
    };

    fetchAudit();
  }, [entityId, entityType, apiEndpoint]);

  if (loading) return <div className="text-gray-500">Loading audit trail...</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  if (!logs.length) return <div className="text-gray-500">No audit records found.</div>;

  const groupedLogs = groupByDate(logs);

  return (
    <div className="space-y-8">
      {Object.entries(groupedLogs).map(([date, dateLogs]) => (
        <div key={date}>
          <h3 className="text-sm font-semibold text-gray-600 mb-3 border-b pb-1">
            {date}
          </h3>

          <div className="space-y-4">
            {dateLogs.map((log) => (
              <div
                key={log._id}
                className="relative pl-6 border-l-2 border-gray-200"
              >
                {/* Timeline dot */}
                <div className="absolute -left-2 top-2 w-3 h-3 bg-teal-500 rounded-full"></div>

                <div className="bg-white shadow-sm border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded ${
                        actionStyles[log.action] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {log.action}
                    </span>

                    <span className="text-xs text-gray-500">
                      {formatDateTime(log.performedAt)}
                    </span>
                  </div>

                  <div className="text-xs text-gray-600 mb-2">
                    {log.performedBy?.name}
                    {log.performedBy?.email && ` (${log.performedBy.email})`}
                  </div>

                  {/* VOID Reason */}
                  {log.action === "VOID" && log.snapshot?.reason && (
                    <div className="text-sm text-red-600 font-medium mt-2">
                      Reason: {log.snapshot.reason}
                    </div>
                  )}

                  {/* UPDATE changes */}
                  {log.action === "UPDATE" && log.changes && (
                    <details className="mt-3 text-xs">
                      <summary className="cursor-pointer font-medium text-gray-600">
                        View Changes
                      </summary>
                      <div className="mt-2 overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-left text-gray-500">
                              <th className="pr-4 py-1">Field</th>
                              <th className="pr-4 py-1">From</th>
                              <th className="pr-4 py-1">To</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(log.changes).map(
                              ([field, value]) => (
                                <tr key={field} className="border-t">
                                  <td className="pr-4 py-1 font-medium">
                                    {field}
                                  </td>
                                  <td className="pr-4 py-1">
                                    {JSON.stringify(value?.from)}
                                  </td>
                                  <td className="pr-4 py-1">
                                    {JSON.stringify(value?.to)}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}

                  {/* CREATE / POST snapshot */}
                  {log.action !== "UPDATE" && log.snapshot && (
                    <details className="mt-3 text-xs">
                      <summary className="cursor-pointer text-gray-600">
                        View Snapshot
                      </summary>
                      <pre className="mt-2 bg-gray-50 p-2 rounded border overflow-auto">
                        {JSON.stringify(log.snapshot, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AuditLogTab;
